// src/views/hrms/leaves/LeavesSummary.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  MdOutlineCalendarToday,
  MdArrowDropUp,
  MdArrowDropDown,
} from "react-icons/md";
import Card from "components/card";
import LineChart from "components/charts/LineChart";
import { getLeaves } from "services/hrms.api";

// ---- date helpers ----
function getMonthRange(date = new Date()) {
  const y = date.getFullYear();
  const m = date.getMonth(); // 0-based
  const start = new Date(y, m, 1, 0, 0, 0, 0);
  const end = new Date(y, m + 1, 0, 23, 59, 59, 999);
  return { start, end };
}

function getPrevMonthRange(date = new Date()) {
  const y = date.getFullYear();
  const m = date.getMonth();
  const start = new Date(y, m - 1, 1, 0, 0, 0, 0);
  const end = new Date(y, m, 0, 23, 59, 59, 999);
  return { start, end };
}

function inRange(dt, range) {
  const d = new Date(dt);
  if (isNaN(d)) return false;
  return d >= range.start && d <= range.end;
}

const LeavesSummary = ({ title = "Leaves Summary", periodLabel }) => {
  const [totalLeaves, setTotalLeaves] = useState(0);
  const [changePct, setChangePct] = useState(0);
  const [counts, setCounts] = useState([]); // daily counts
  const [categories, setCategories] = useState([]); // "1", "2", ...
  const [isDark, setIsDark] = useState(false);
  const [statusCounts, setStatusCounts] = useState({
    approved: 0,
    pending: 0,
    rejected: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const now = useMemo(() => new Date(), []);
  const monthShort = useMemo(
    () => now.toLocaleString("en-GB", { month: "short" }),
    [now]
  );
  const defaultPeriod = useMemo(
    () =>
      now.toLocaleString("en-GB", {
        month: "short",
        year: "numeric",
      }),
    [now]
  );

  // ---- Fetch & compute stats ----
  useEffect(() => {
    setIsDark(localStorage.getItem("theme") === "dark" ? true : false);
    (async () => {
      try {
        setError("");
        setLoading(true);

        const { data } = await getLeaves();
        const list = Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data)
            ? data
            : [];

        const currentRange = getMonthRange(now);
        const prevRange = getPrevMonthRange(now);

        const daysInMonth = getMonthRange(now).end.getDate();
        const dailyCounts = new Array(daysInMonth).fill(0);
        const cats = Array.from({ length: daysInMonth }, (_, i) =>
          String(i + 1)
        );

        let currentCount = 0;
        let prevCount = 0;

        let approved = 0;
        let pending = 0;
        let rejected = 0;

        for (const row of list) {
          // Prefer appliedAt → then createdAt → then fromDate
          const dateRef = row.appliedAt || row.createdAt || row.fromDate;
          if (!dateRef) continue;

          const d = new Date(dateRef);
          if (isNaN(d)) continue;

          const status = String(row.status || "").toLowerCase();

          // current month
          if (inRange(d, currentRange)) {
            currentCount += 1;

            const idx = d.getDate() - 1;
            if (idx >= 0 && idx < dailyCounts.length) {
              dailyCounts[idx] += 1;
            }

            if (status === "approved") approved += 1;
            else if (status === "pending") pending += 1;
            else if (status === "rejected" || status === "cancelled")
              rejected += 1;
          } else if (inRange(d, prevRange)) {
            // previous month
            prevCount += 1;
          }
        }

        setTotalLeaves(currentCount);
        setStatusCounts({ approved, pending, rejected });

        let pct = 0;
        if (prevCount > 0) {
          pct = ((currentCount - prevCount) / prevCount) * 100;
        } else if (prevCount === 0 && currentCount > 0) {
          pct = 100;
        } else {
          pct = 0;
        }

        setChangePct(Number.isFinite(pct) ? Number(pct.toFixed(1)) : 0);
        setCounts(dailyCounts);
        setCategories(cats);
      } catch (e) {
        console.error("[LeavesSummary] load error:", e);
        setError(e.message || "Failed to load leaves summary");
        setTotalLeaves(0);
        setChangePct(0);
        setCounts([]);
        setCategories([]);
        setStatusCounts({ approved: 0, pending: 0, rejected: 0 });
      } finally {
        setLoading(false);
      }
    })();
  }, [now]);

  // ---- Chart config: readable + attractive ----
  const series = useMemo(
    () => [
      {
        name: "Daily requests",
        data: counts.length ? counts : [0, 0, 0, 0, 0],
      },
    ],
    [counts]
  );

  const options = useMemo(
    () => ({
      chart: {
        type: "area",
        toolbar: { show: false },
        zoom: { enabled: false },
        background: "transparent",
      },
      theme: {
        mode: isDark ? "dark" : "light",
      },
      stroke: {
        curve: "smooth",
        width: 3,
      },
      dataLabels: { enabled: false },
      markers: {
        size: 4,
        strokeWidth: 1,
        strokeColors: isDark ? "#020617" : "#ffffff",
      },
      tooltip: {
        theme: isDark ? "dark" : "light",
        x: {
          formatter: (val, opts) => {
            const idx = opts?.dataPointIndex ?? 0;
            const day = categories[idx] || val;
            return `${day} ${monthShort}`;
          },
        },
        y: {
          formatter: (v) => `${v} request${v === 1 ? "" : "s"}`,
        },
      },

      // 🔹 line + gradient colors
      colors: [isDark ? "#38BDF8" : "#6366F1"], // line color
      fill: {
        type: "gradient",
        gradient: {
          shade: "light",
          shadeIntensity: 0.4,
          gradientToColors: [isDark ? "#0EA5E9" : "#EC4899"],
          inverseColors: false,
          opacityFrom: isDark ? 0.6 : 0.5,
          opacityTo: 0.05,
          stops: [0, 40, 80, 100],
        },
      },

      grid: {
        show: true,
        borderColor: isDark
          ? "rgba(148, 163, 184, 0.25)"
          : "rgba(148, 163, 184, 0.15)",
        strokeDashArray: 4,
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
      },
      xaxis: {
        categories,
        tickAmount: Math.min(6, Math.max(1, categories.length - 1)),
        labels: {
          style: {
            fontSize: "10px",
            colors: isDark ? "#E5E7EB" : "#9CA3AF",
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        min: 0,
        labels: {
          style: {
            fontSize: "10px",
            colors: isDark ? "#E5E7EB" : "#9CA3AF",
          },
        },
      },
    }),
    [categories, monthShort, isDark]
  );

  const UpOrDown = changePct >= 0 ? MdArrowDropUp : MdArrowDropDown;
  const changeColor = changePct >= 0 ? "text-emerald-600" : "text-rose-600";

  return (
    <Card extra="!p-[50px]">
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex flex-col text-left">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-white">
            {title}
          </h3>
          <div className="mt-1 inline-flex items-center gap-2 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:bg-white/5 dark:text-white/70">
            <MdOutlineCalendarToday className="h-3 w-3" />
            {periodLabel || defaultPeriod}
          </div>
        </div>

        {/* <button className="!linear z-[1] flex items-center justify-center rounded-lg bg-lightPrimary p-2 text-brand-500 !transition !duration-200 hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:hover:bg-white/20">
          <MdBarChart className="h-5 w-5" />
        </button> */}
      </div>

      {/* Main content row */}
      <div className="flex h-full w-full flex-row justify-between gap-4 sm:flex-wrap lg:flex-nowrap">
        {/* Left stats */}
        <div className="flex min-w-[170px] flex-col">
          <p className="mt-[10px] text-3xl font-bold text-navy-700 dark:text-white">
            {loading ? "…" : totalLeaves}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-white/60">
            Total leave requests applied this month
          </p>

          {error ? (
            <p className="text-rose-500 dark:text-rose-300 mt-2 text-xs">
              {error}
            </p>
          ) : (
            <div className="mt-3 flex items-center gap-1">
              <UpOrDown className={`h-6 w-7 ${changeColor}`} />
              <p className={`text-sm font-semibold ${changeColor}`}>
                {changePct >= 0 ? `+${changePct}%` : `${changePct}%`}
              </p>
              <span className="text-xs text-gray-500 dark:text-white/60">
                vs last month
              </span>
            </div>
          )}

          {/* Status breakdown chips */}
          <div className="mt-4 flex flex-wrap gap-2 text-[11px]">
            <span className="bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200 inline-flex items-center gap-2 rounded-full px-2 py-1">
              <span className="bg-emerald-500 h-2 w-2 rounded-full" />
              Approved: {statusCounts.approved}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-2 py-1 text-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
              <span className="h-2 w-2 rounded-full bg-amber-500" />
              Pending: {statusCounts.pending}
            </span>
            <span className="bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-200 inline-flex items-center gap-2 rounded-full px-2 py-1">
              <span className="bg-rose-500 h-2 w-2 rounded-full" />
              Rejected: {statusCounts.rejected}
            </span>
          </div>
        </div>

        {/* Right chart */}
        <div className="h-[150px] w-full sm:h-[170px]">
          <LineChart options={options} series={series} />
        </div>
      </div>
    </Card>
  );
};

export default LeavesSummary;
